import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { 
  FileText, 
  Download, 
  Trash2, 
  Archive, 
  ArchiveRestore, 
  Edit, 
  Search,
  Eye,
  EyeOff
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface Document {
  id: string;
  file_name: string;
  file_type: string;
  file_size: number;
  file_path: string;
  category: string;
  notes: string | null;
  is_archived: boolean;
  archived_at: string | null;
  created_at: string;
}

interface DocumentListProps {
  dealId: string;
  documents: Document[];
  onRefresh: () => void;
}

const DOCUMENT_CATEGORIES = [
  "Contract",
  "Appraisal",
  "Title Insurance",
  "Correspondence",
  "Photos",
  "Financial Documents",
  "Legal Documents",
  "Other"
];

export function DocumentList({ dealId, documents, onRefresh }: DocumentListProps) {
  const [showArchived, setShowArchived] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [editingDoc, setEditingDoc] = useState<Document | null>(null);
  const [editCategory, setEditCategory] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [deletingDoc, setDeletingDoc] = useState<Document | null>(null);
  const { toast } = useToast();

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const filteredDocs = documents.filter(doc => {
    const matchesSearch = doc.file_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         doc.category.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesArchive = showArchived || !doc.is_archived;
    return matchesSearch && matchesArchive;
  });

  const groupedDocs = filteredDocs.reduce((acc, doc) => {
    if (!acc[doc.category]) acc[doc.category] = [];
    acc[doc.category].push(doc);
    return acc;
  }, {} as Record<string, Document[]>);

  const handleDownload = async (doc: Document) => {
    try {
      const { data, error } = await supabase.storage
        .from('deal-documents')
        .download(doc.file_path);

      if (error) throw error;

      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({
        title: "Download started",
        description: doc.file_name
      });
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: "Download failed",
        description: "Could not download the file",
        variant: "destructive"
      });
    }
  };

  const handleArchive = async (doc: Document) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from('deal_documents')
        .update({
          is_archived: !doc.is_archived,
          archived_at: !doc.is_archived ? new Date().toISOString() : null,
          archived_by: !doc.is_archived ? user?.id : null
        })
        .eq('id', doc.id);

      if (error) throw error;

      toast({
        title: doc.is_archived ? "Document restored" : "Document archived",
        description: doc.file_name
      });
      onRefresh();
    } catch (error) {
      console.error('Archive error:', error);
      toast({
        title: "Operation failed",
        description: "Could not update document status",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async () => {
    if (!deletingDoc) return;

    try {
      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('deal-documents')
        .remove([deletingDoc.file_path]);

      if (storageError) throw storageError;

      // Delete from database
      const { error: dbError } = await supabase
        .from('deal_documents')
        .delete()
        .eq('id', deletingDoc.id);

      if (dbError) throw dbError;

      toast({
        title: "Document deleted",
        description: deletingDoc.file_name
      });
      setDeletingDoc(null);
      onRefresh();
    } catch (error) {
      console.error('Delete error:', error);
      toast({
        title: "Delete failed",
        description: "Could not delete the document",
        variant: "destructive"
      });
    }
  };

  const handleEditSave = async () => {
    if (!editingDoc) return;

    try {
      const { error } = await supabase
        .from('deal_documents')
        .update({
          category: editCategory,
          notes: editNotes || null
        })
        .eq('id', editingDoc.id);

      if (error) throw error;

      toast({
        title: "Document updated",
        description: editingDoc.file_name
      });
      setEditingDoc(null);
      onRefresh();
    } catch (error) {
      console.error('Update error:', error);
      toast({
        title: "Update failed",
        description: "Could not update document",
        variant: "destructive"
      });
    }
  };

  const openEditDialog = (doc: Document) => {
    setEditingDoc(doc);
    setEditCategory(doc.category);
    setEditNotes(doc.notes || "");
  };

  const archivedCount = documents.filter(d => d.is_archived).length;
  const activeCount = documents.length - archivedCount;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search documents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button
          variant="outline"
          onClick={() => setShowArchived(!showArchived)}
          className="flex items-center gap-2"
        >
          {showArchived ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          {showArchived ? "Hide Archived" : `Show Archived (${archivedCount})`}
        </Button>
      </div>

      <div className="flex items-center gap-4 text-sm text-muted-foreground">
        <span>Active: {activeCount}</span>
        {archivedCount > 0 && <span>Archived: {archivedCount}</span>}
        <span>Total Size: {formatFileSize(documents.reduce((sum, d) => sum + d.file_size, 0))}</span>
      </div>

      {Object.keys(groupedDocs).length === 0 ? (
        <Card>
          <CardContent className="pt-6 text-center text-muted-foreground">
            {searchQuery ? "No documents match your search" : "No documents uploaded yet"}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {Object.entries(groupedDocs).map(([category, docs]) => (
            <Card key={category}>
              <CardHeader>
                <CardTitle className="text-lg flex items-center justify-between">
                  {category}
                  <Badge variant="secondary">{docs.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {docs.map(doc => (
                  <div
                    key={doc.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border ${
                      doc.is_archived ? 'bg-muted/50 border-muted' : 'bg-card'
                    }`}
                  >
                    <FileText className="h-5 w-5 flex-shrink-0 text-muted-foreground" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium truncate">{doc.file_name}</p>
                        {doc.is_archived && (
                          <Badge variant="outline" className="text-xs">Archived</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span>{formatFileSize(doc.file_size)}</span>
                        <span>•</span>
                        <span>{formatDate(doc.created_at)}</span>
                        {doc.notes && (
                          <>
                            <span>•</span>
                            <span className="truncate max-w-xs">{doc.notes}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDownload(doc)}
                        title="Download"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog(doc)}
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleArchive(doc)}
                        title={doc.is_archived ? "Restore" : "Archive"}
                      >
                        {doc.is_archived ? (
                          <ArchiveRestore className="h-4 w-4" />
                        ) : (
                          <Archive className="h-4 w-4" />
                        )}
                      </Button>
                      {doc.is_archived && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeletingDoc(doc)}
                          title="Delete permanently"
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={!!editingDoc} onOpenChange={() => setEditingDoc(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Document</DialogTitle>
            <DialogDescription>
              Update the category and notes for {editingDoc?.file_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={editCategory} onValueChange={setEditCategory}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DOCUMENT_CATEGORIES.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingDoc(null)}>
              Cancel
            </Button>
            <Button onClick={handleEditSave}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletingDoc} onOpenChange={() => setDeletingDoc(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently Delete Document?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <strong>{deletingDoc?.file_name}</strong> ({deletingDoc && formatFileSize(deletingDoc.file_size)}).
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
